/** Parse template QR token from deep link URL or raw scan text. */

export const CHECKLIST_QR_MISMATCH_STORAGE_KEY = 'checklist_qr_mismatch_message';

const SCAN_PATH = '/checklists/scan';
const WALLET_SCAN_PATH = '/wallet/scan';
const COUNTERPARTY_SCAN_PATH = '/counterparties/scan';
const QR_DEEP_LINK_PATH_KEY = 'pwa-qr-deeplink-path';

export const WALLET_QR_LAST_TOKEN_KEY = 'pwa-wallet-qr-last-token';
export const COUNTERPARTY_QR_LAST_TOKEN_KEY = 'pwa-counterparty-qr-last-token';

export function parseChecklistQrToken(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;
  try {
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      const u = new URL(raw);
      const t = u.searchParams.get('t');
      if (t) return t.trim();
    }
  } catch {
    /* not a URL */
  }
  if (/^[A-Za-z0-9_-]{8,80}$/.test(raw)) return raw;
  return null;
}

export function extractScanTokenFromHref(hrefOrPath: string): string | null {
  const raw = (hrefOrPath || '').trim();
  if (!raw) return null;
  try {
    const u = raw.startsWith('http://') || raw.startsWith('https://') || raw.includes('://')
      ? new URL(raw)
      : new URL(raw, 'https://app.pick-pvz.ru');
    const t = u.searchParams.get('t')?.trim();
    return t || null;
  } catch {
    return null;
  }
}

function sessionGet(key: string): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function sessionSet(key: string, value: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ignore quota / private mode */
  }
}

function sessionRemove(key: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function rememberQrDeepLinkPath(path: string): void {
  const clean = (path || '').trim();
  if (!clean.startsWith('/')) return;
  sessionSet(QR_DEEP_LINK_PATH_KEY, clean);
}

export function peekQrDeepLinkPath(): string | null {
  const raw = sessionGet(QR_DEEP_LINK_PATH_KEY)?.trim();
  return raw && raw.startsWith('/') ? raw : null;
}

export function consumeQrDeepLinkPathIfMatches(prefix: string): void {
  const pending = peekQrDeepLinkPath();
  if (pending && pending.startsWith(prefix)) {
    sessionRemove(QR_DEEP_LINK_PATH_KEY);
  }
}

function isShellHomePath(path: string): boolean {
  const pathname = path.split('?')[0].split('#')[0];
  return !pathname || pathname === '/';
}

function pathWithSearch(pathname: string, search: string): string {
  return `${pathname || ''}${search || ''}`;
}

/**
 * Normalizes external / native URLs to in-app router paths.
 * Examples:
 * - https://app.pick-pvz.ru/checklists/scan?t=abc → /checklists/scan?t=abc
 * - pickupcontrol://checklists/scan?t=abc → /checklists/scan?t=abc
 * - https://app.pick-pvz.ru/?cap_native=1 → null (Capacitor shell home)
 */
export function resolveAppDeepLinkPath(urlOrPath: string): string | null {
  const raw = (urlOrPath || '').trim();
  if (!raw) return null;

  if (raw.startsWith('/')) {
    if (isShellHomePath(raw)) return null;
    return raw;
  }

  try {
    const u = new URL(raw);
    const protocol = (u.protocol || '').replace(/:$/, '');
    if (protocol === 'pickupcontrol') {
      const host = (u.hostname || '').toLowerCase();
      if (host === 'checklists' || host === 'wallet' || host === 'counterparties') {
        const rest = `${u.pathname || ''}${u.search || ''}`;
        const path = `/${host}${rest.startsWith('/') ? rest : `/${rest}`}`;
        return isShellHomePath(path) ? null : path;
      }
    }
    const path = pathWithSearch(u.pathname || '', u.search || '');
    if (isShellHomePath(path)) return null;
    if (
      path.startsWith(SCAN_PATH) ||
      path.startsWith(WALLET_SCAN_PATH) ||
      path.startsWith(COUNTERPARTY_SCAN_PATH)
    ) {
      return path;
    }
    if (path.startsWith('/checklists') || path.startsWith('/wallet') || path.startsWith('/counterparties')) {
      return path;
    }
    return path;
  } catch {
    if (raw.includes('checklists/scan')) {
      const idx = raw.indexOf('/checklists/scan');
      return idx >= 0 ? raw.slice(idx) : null;
    }
    if (raw.includes('wallet/scan')) {
      const idx = raw.indexOf('/wallet/scan');
      return idx >= 0 ? raw.slice(idx) : null;
    }
    if (raw.includes('counterparties/scan')) {
      const idx = raw.indexOf('/counterparties/scan');
      return idx >= 0 ? raw.slice(idx) : null;
    }
  }
  return null;
}

type SearchParamsLike = { get: (key: string) => string | null } | null | undefined;

/**
 * Token already present in the scan URL / deep-link memory.
 * If this returns a value, native auto-scan must not start.
 */
export function readIncomingScanToken(
  searchParams: SearchParamsLike,
  scanPathPrefix: string,
): string | null {
  const fromParams = searchParams?.get('t')?.trim();
  if (fromParams) return fromParams;

  if (typeof window !== 'undefined') {
    try {
      const href = window.location.href;
      if (window.location.pathname.startsWith(scanPathPrefix)) {
        const fromHref = extractScanTokenFromHref(href);
        if (fromHref) return fromHref;
      }
    } catch {
      /* ignore */
    }
  }

  const pending = peekQrDeepLinkPath();
  if (pending && pending.startsWith(scanPathPrefix)) {
    return extractScanTokenFromHref(pending);
  }
  return null;
}

export function shouldAutoStartNativeQrScan(options: {
  isNative: boolean;
  incomingToken: string | null;
  alreadyStarted: boolean;
  pickMode?: boolean;
}): boolean {
  if (!options.isNative || options.alreadyStarted || options.incomingToken || options.pickMode) {
    return false;
  }
  return true;
}

export function isChecklistScanDeepLink(path: string): boolean {
  return path.startsWith(SCAN_PATH);
}
